/*
 * Copyright (c) 2024 Bolmate B.V.
 */
import './style/platformUsage.scss';

import React, {useMemo} from 'react';
import {AwesomeIcon, Checkbox, CloseButton, ElementPosition, Loader, WithBackground} from '@dvrd/dvr-controls';
import Chart from 'react-apexcharts';
import {ApexOptions} from 'apexcharts';
import IDate from '@dvrd/idate';
import {BolmateButton, BolmateInput, BolmateSelect, BolmateNumberInput} from '../../controls';
import InfoIcon from '../../icon/infoIcon';
import {
    AnalyticsSettings,
    BucketOption,
    BreakdownOption,
    EditableSettingsState,
    EventNameOption,
    FilterState,
    MetricOption,
    PlatformUsageData
} from './platformUsageController';

interface Props {
    loading: boolean;
    data: PlatformUsageData | null;
    filters: FilterState;
    isDayBucketAllowed: boolean;
    onUpdateFilter: <K extends keyof FilterState>(key: K, value: FilterState[K]) => void;
    onDrillDown: (featureKey: string) => void;
    onClearDrillDown: () => void;
    onResetFilters: () => void;
    onRefresh: () => void;
    onRunAggregation: () => void;
    cooldownRemainingSeconds: number;
    analyticsSettings: AnalyticsSettings | null;
    editableSettings: EditableSettingsState | null;
    settingsLoading: boolean;
    settingsSaving: boolean;
    settingsHasChanges: boolean;
    clearDataModalActive: boolean;
    clearDataConfirmed: boolean;
    clearDataLoading: boolean;
    onUpdateEditableSetting: <K extends keyof EditableSettingsState>(key: K, value: EditableSettingsState[K]) => void;
    onSaveSettings: () => void;
    onOpenClearDataModal: () => void;
    onCloseClearDataModal: () => void;
    onSetClearDataConfirmed: (confirmed: boolean) => void;
    onClearAllData: () => void;
}

const BUCKET_OPTIONS: {value: BucketOption; label: string}[] = [
    {value: 'auto', label: 'Auto'},
    {value: 'day', label: 'Dag'},
    {value: 'week', label: 'Week'},
    {value: 'month', label: 'Maand'}
];

const METRIC_OPTIONS: {value: MetricOption; label: string}[] = [
    {value: 'unique_users', label: 'Unieke gebruikers'},
    {value: 'total_events', label: 'Totaal events'}
];

const EVENT_OPTIONS: {value: EventNameOption; label: string}[] = [
    {value: 'page_view', label: 'Page views'},
    {value: 'action_success', label: 'Acties (succes)'},
    {value: 'action_failed', label: 'Acties (gefaald)'}
];

const BREAKDOWN_OPTIONS: {value: BreakdownOption; label: string}[] = [
    {value: 'feature_group', label: 'Feature groep'},
    {value: 'feature_key', label: 'Feature'}
];

const TOP_N_OPTIONS: {value: number; label: string}[] = [
    {value: 5, label: '5'},
    {value: 10, label: '10'},
    {value: 0, label: 'Alles'}
];

// Chart helper functions
interface YAxisConfig {
    min: number;
    max: number;
    tickAmount: number;
}

function buildYAxisConfig(values: number[], options?: {padding?: number; startFromZero?: boolean}): YAxisConfig {
    const {padding = 0.1, startFromZero = true} = options ?? {};
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values, 1);
    const yAxisMin = startFromZero ? 0 : Math.max(0, Math.floor(minValue * (1 - padding)));
    const yAxisMax = Math.ceil(maxValue * (1 + padding));
    const range = yAxisMax - yAxisMin;
    const tickAmount = Math.min(Math.max(range, 2), 10);
    return {min: yAxisMin, max: yAxisMax, tickAmount};
}

function buildBaseChartOptions(
    categories: string[],
    yAxisConfig: YAxisConfig,
    title: string,
    chartType: 'line' | 'area' = 'line',
    overrides?: Partial<ApexOptions>
): ApexOptions {
    return {
        chart: {
            type: chartType,
            toolbar: {show: false},
            zoom: {enabled: false}
        },
        xaxis: {
            categories,
            labels: {rotate: -45, rotateAlways: categories.length > 10}
        },
        yaxis: {
            min: yAxisConfig.min,
            max: yAxisConfig.max,
            tickAmount: yAxisConfig.tickAmount,
            reversed: false,
            labels: {
                formatter: (val: number) => Math.round(val).toString()
            }
        },
        stroke: {curve: 'smooth', width: 2},
        markers: {
            size: categories.length <= 3 ? 6 : 4,
            strokeWidth: 2,
            hover: {size: 8}
        },
        dataLabels: {enabled: false},
        title: {
            text: title,
            align: 'left',
            style: {fontSize: '14px', fontWeight: 600}
        },
        ...overrides
    };
}

export default function PlatformUsage(props: Props) {
    const {
        loading,
        data,
        filters,
        isDayBucketAllowed,
        onUpdateFilter,
        onDrillDown,
        onClearDrillDown,
        onResetFilters,
        onRefresh,
        onRunAggregation,
        cooldownRemainingSeconds,
        analyticsSettings,
        editableSettings,
        settingsLoading,
        settingsSaving,
        settingsHasChanges,
        clearDataModalActive,
        clearDataConfirmed,
        clearDataLoading,
        onUpdateEditableSetting,
        onSaveSettings,
        onOpenClearDataModal,
        onCloseClearDataModal,
        onSetClearDataConfirmed,
        onClearAllData
    } = props;

    // Today's date for max date restriction (cannot select future dates)
    const today = useMemo(() => new IDate().format('YYYY-MM-DD'), []);

    // Has any data in the system at all (independent of filters)
    const hasAnyData = data?.has_any_data ?? false;
    // Has results for the current filter settings
    const hasFilteredResults = data?.timeseries_global && data.timeseries_global.length > 0;
    // Check if date range is valid (fromDate <= toDate)
    const isDateRangeValid = filters.fromDate <= filters.toDate;

    function onClickBack() {
        history.back();
    }

    function renderStatRow(label: string, value: number) {
        return (
            <div className='stat-row' key={label}>
                <span className='stat-label' title={label}>{label}</span>
                <span className='stat-value'>{value}</span>
            </div>
        );
    }

    function renderStatRowWithActive(label: string, valueAll: number, valueActive: number) {
        return (
            <div className='stat-row' key={label}>
                <span className='stat-label' title={label}>{label}</span>
                <span className='stat-value'>{valueAll}</span>
                <span className='stat-value active'>{valueActive}</span>
            </div>
        );
    }

    function renderEmailStats() {
        if (!data) return null;
        const {email_stats} = data;
        return (
            <div className='stats-section'>
                <div className='section-header'>
                    <h3>E-mail instellingen</h3>
                    <div className='section-info-container'>
                        <InfoIcon scale={0.6} />
                        <div className='section-info-tooltip'>
                            <strong>E-mail instellingen</strong>
                            <p>Overzicht van e-mailinstellingen per account.</p>
                            <ul>
                                <li><strong>Automatisch mailen aan/uit:</strong> Of automatische e-mailverzending is ingeschakeld (minstens één actieve e-mail flow).</li>
                                <li><strong>No-reply tracking:</strong> Of no-reply tracking (met leesbevestigingen) is ingeschakeld.</li>
                                <li><strong>Alleen factuur e-mail:</strong> Accounts die alleen factuur e-mails versturen.</li>
                                <li><strong>Factuur + review verzoek:</strong> Accounts die zowel factuur als review e-mails versturen.</li>
                            </ul>
                            <p className='column-explanation'>
                                <strong>Alle:</strong> Alle accounts.<br/>
                                <strong>Actief:</strong> Alleen betalende, trial en gratis accounts (exclusief geannuleerd/inactief).
                            </p>
                        </div>
                    </div>
                </div>
                <div className='stats-table with-active'>
                    <div className='stat-row header'>
                        <span className='stat-label'></span>
                        <span className='stat-value'>Alle</span>
                        <span className='stat-value active'>Actief</span>
                    </div>
                    {renderStatRowWithActive('Automatisch mailen aan', email_stats.auto_mail_enabled, email_stats.auto_mail_enabled_active)}
                    {renderStatRowWithActive('Automatisch mailen uit', email_stats.auto_mail_disabled, email_stats.auto_mail_disabled_active)}
                    {renderStatRowWithActive('No-reply tracking aan', email_stats.email_tracking_enabled, email_stats.email_tracking_enabled_active)}
                    {renderStatRowWithActive('No-reply tracking uit', email_stats.email_tracking_disabled, email_stats.email_tracking_disabled_active)}
                    {renderStatRowWithActive('Alleen factuur e-mail', email_stats.invoice_only, email_stats.invoice_only_active)}
                    {renderStatRowWithActive('Factuur + review verzoek', email_stats.invoice_and_review, email_stats.invoice_and_review_active)}
                </div>
            </div>
        );
    }

    function renderNotificationStats() {
        if (!data) return null;
        const {by_type, by_type_active, module_paid, module_trial, module_total} = data.notification_stats;
        return (
            <>
                <div className='stats-section'>
                    <div className='section-header'>
                        <h3>Notificaties (per type)</h3>
                        <div className='section-info-container'>
                            <InfoIcon scale={0.6} />
                            <div className='section-info-tooltip'>
                                <strong>Notificaties per type</strong>
                                <p>Aantal accounts met actieve notificaties per type.</p>
                                <ul>
                                    <li><strong>Reviews:</strong> Notificaties over nieuwe reviews.</li>
                                    <li><strong>Buy Box:</strong> Notificaties over Buy Box wijzigingen.</li>
                                    <li><strong>Stock:</strong> Notificaties over voorraadwijzigingen.</li>
                                    <li><strong>Order (gratis):</strong> Gratis ordernotificaties.</li>
                                </ul>
                                <p className='column-explanation'>
                                    <strong>Alle:</strong> Alle accounts.<br/>
                                    <strong>Actief:</strong> Alleen betalende, trial en gratis accounts (exclusief geannuleerd/inactief).
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className='stats-table with-active'>
                        <div className='stat-row header'>
                            <span className='stat-label'></span>
                            <span className='stat-value'>Alle</span>
                            <span className='stat-value active'>Actief</span>
                        </div>
                        {renderStatRowWithActive('Reviews', by_type.REVIEWS, by_type_active.REVIEWS)}
                        {renderStatRowWithActive('Buy Box', by_type.BUY_BOX, by_type_active.BUY_BOX)}
                        {renderStatRowWithActive('Stock', by_type.STOCK, by_type_active.STOCK)}
                        {renderStatRowWithActive('Order (gratis)', by_type.ORDER, by_type_active.ORDER)}
                    </div>
                </div>
                <div className='stats-section'>
                    <div className='section-header'>
                        <h3>Notificaties module (abonnement)</h3>
                        <div className='section-info-container'>
                            <InfoIcon scale={0.6} />
                            <div className='section-info-tooltip'>
                                <strong>Notificaties module</strong>
                                <p>Status van de betaalde notificatiemodule per abonnement.</p>
                                <ul>
                                    <li><strong>Na trial (betalend):</strong> Abonnementen die betalen voor notificaties (na trial periode).</li>
                                    <li><strong>In trial:</strong> Abonnementen die de notificatiemodule in trial hebben.</li>
                                    <li><strong>Totaal actief:</strong> Totaal aantal actieve notificatiemodules (betalend + trial + gratis, exclusief geannuleerd).</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div className='stats-table'>
                        {renderStatRow('Na trial (betalend)', module_paid)}
                        {renderStatRow('In trial', module_trial)}
                        {renderStatRow('Totaal actief', module_total)}
                    </div>
                </div>
            </>
        );
    }

    function renderAdditionalUsersStats() {
        if (!data) return null;
        const {additional_users_stats} = data;
        return (
            <div className='stats-section'>
                <div className='section-header'>
                    <h3>Extra gebruikers</h3>
                    <div className='section-info-container'>
                        <InfoIcon scale={0.6} />
                        <div className='section-info-tooltip'>
                            <strong>Extra gebruikers</strong>
                            <p>Overzicht van accounts met meerdere gebruikers (naast de eigenaar).</p>
                            <ul>
                                <li><strong>Accounts met extra gebruikers:</strong> Aantal accounts die meer dan 1 gebruiker hebben.</li>
                                <li><strong>Totaal extra gebruikers:</strong> Som van alle extra gebruikers (exclusief eigenaren).</li>
                            </ul>
                            <p className='column-explanation'>
                                <strong>Alle:</strong> Alle accounts.<br/>
                                <strong>Actief:</strong> Alleen betalende, trial en gratis accounts (exclusief geannuleerd/inactief).
                            </p>
                        </div>
                    </div>
                </div>
                <div className='stats-table with-active'>
                    <div className='stat-row header'>
                        <span className='stat-label'></span>
                        <span className='stat-value'>Alle</span>
                        <span className='stat-value active'>Actief</span>
                    </div>
                    {renderStatRowWithActive(
                        'Accounts met extra gebruikers',
                        additional_users_stats.accounts_with_additional_users,
                        additional_users_stats.accounts_with_additional_users_active
                    )}
                    {renderStatRowWithActive(
                        'Totaal extra gebruikers',
                        additional_users_stats.total_additional_users,
                        additional_users_stats.total_additional_users_active
                    )}
                </div>
            </div>
        );
    }

    function renderFilters() {
        return (
            <div className='analytics-filters'>
                <div className='filters-header'>
                    <span className='filters-title'>Filters</span>
                    <BolmateButton
                        onClick={onResetFilters}
                        label='Filters resetten'
                        secondary
                        ornaments={{element: <AwesomeIcon name='undo' />, placement: ElementPosition.LEFT}}
                    />
                </div>
                <div className='filter-row'>
                    <div className='filter-group'>
                        <label>Van</label>
                        <BolmateInput
                            value={filters.fromDate}
                            onChange={(value: string) => onUpdateFilter('fromDate', value)}
                            inputProps={{type: 'date', max: filters.toDate || today}}
                        />
                    </div>
                    <div className='filter-group'>
                        <label>Tot</label>
                        <BolmateInput
                            value={filters.toDate}
                            onChange={(value: string) => onUpdateFilter('toDate', value)}
                            inputProps={{type: 'date', min: filters.fromDate, max: today}}
                        />
                    </div>
                    <div className='filter-group'>
                        <div className='label-with-help'>
                            <label>Periode</label>
                            <div className='filters-help-container'>
                                <InfoIcon scale={0.6} />
                                <div className='filters-help-tooltip'>
                                    <strong>Auto:</strong> Kiest automatisch de beste periode op basis van het
                                    datumbereik.
                                    <br />
                                    <strong>Dag:</strong> Toont data per dag (alleen beschikbaar bij een bereik van
                                    maximaal 31 dagen).
                                    <br />
                                    <strong>Week:</strong> Groepeert data per week.
                                    <br />
                                    <strong>Maand:</strong> Groepeert data per maand.
                                </div>
                            </div>
                        </div>
                        <BolmateSelect
                            value={filters.bucket}
                            onChange={(value: string) => onUpdateFilter('bucket', value as BucketOption)}
                            items={BUCKET_OPTIONS.map(opt => ({
                                value: opt.value,
                                label: opt.value === 'day' && !isDayBucketAllowed ? `${opt.label} (max 31 dagen)` : opt.label,
                                disabled: opt.value === 'day' && !isDayBucketAllowed
                            }))}
                        />
                    </div>
                    <div className='filter-group'>
                        <div className='label-with-help'>
                            <label>Metriek</label>
                            <div className='filters-help-container'>
                                <InfoIcon scale={0.6} />
                                <div className='filters-help-tooltip'>
                                    <strong>Unieke gebruikers:</strong> Telt hoeveel verschillende personen de pagina
                                    hebben bezocht.
                                    <br />
                                    <strong>Totaal events:</strong> Telt elk paginabezoek afzonderlijk.
                                </div>
                            </div>
                        </div>
                        <BolmateSelect
                            value={filters.metric}
                            onChange={(value: string) => onUpdateFilter('metric', value as MetricOption)}
                            items={METRIC_OPTIONS}
                        />
                    </div>
                </div>
                <div className='filter-row'>
                    <div className='filter-group'>
                        <div className='label-with-help'>
                            <label>Event type</label>
                            <div className='filters-help-container'>
                                <InfoIcon scale={0.6} />
                                <div className='filters-help-tooltip'>
                                    <strong>Page views:</strong> Paginabezoeken - wanneer een gebruiker een pagina
                                    opent.
                                    <br />
                                    <strong>Acties (succes):</strong> Succesvolle acties zoals opslaan, exporteren,
                                    versturen.
                                    <br />
                                    <strong>Acties (gefaald):</strong> Mislukte acties (bijv. validatiefouten).
                                </div>
                            </div>
                        </div>
                        <BolmateSelect
                            value={filters.eventName}
                            onChange={(value: string) => onUpdateFilter('eventName', value as EventNameOption)}
                            items={EVENT_OPTIONS}
                        />
                    </div>
                    <div className='filter-group'>
                        <div className='label-with-help'>
                            <label>Groeperen op</label>
                            <div className='filters-help-container'>
                                <InfoIcon scale={0.6} />
                                <div className='filters-help-tooltip'>
                                    <strong>Feature groep:</strong> Groepeert per categorie (bijv. Settings, Invoices,
                                    Emails).
                                    <br />
                                    <strong>Feature:</strong> Toont individuele functies (bijv. settings.api,
                                    invoices.list).
                                </div>
                            </div>
                        </div>
                        <BolmateSelect
                            value={filters.breakdown}
                            onChange={(value: string) => onUpdateFilter('breakdown', value as BreakdownOption)}
                            items={BREAKDOWN_OPTIONS}
                        />
                    </div>
                    <div className='filter-group'>
                        <div className='label-with-help'>
                            <label>Top N</label>
                            <div className='filters-help-container'>
                                <InfoIcon scale={0.6} />
                                <div className='filters-help-tooltip'>
                                    Bepaalt hoeveel items worden weergegeven in de grafiek en tabel.
                                    <br />
                                    <strong>Alles:</strong> Toont alle beschikbare items.
                                </div>
                            </div>
                        </div>
                        <BolmateSelect
                            value={String(filters.topN)}
                            onChange={(value: string) => onUpdateFilter('topN', parseInt(value))}
                            items={TOP_N_OPTIONS.map(opt => ({value: String(opt.value), label: opt.label}))}
                        />
                    </div>
                    <div className='filter-group user-search'>
                        <div className='label-with-help'>
                            <label>Gebruiker zoeken</label>
                            <div className='filters-help-container'>
                                <InfoIcon scale={0.6} />
                                <div className='filters-help-tooltip'>
                                    Zoekt in: e-mailadres, voornaam, achternaam, telefoonnummer en bedrijfsnaam van het
                                    account.
                                </div>
                            </div>
                        </div>
                        <BolmateInput
                            value={filters.userSearch}
                            onChange={(value: string) => onUpdateFilter('userSearch', value)}
                            inputProps={{placeholder: 'E-mail, naam, telefoon...'}}
                        />
                    </div>
                </div>
                <div className='filter-row user-type-filters'>
                    <span className='filter-row-label'>Gebruikertype:</span>
                    <div className='filters-help-container'>
                        <InfoIcon scale={0.6} />
                        <div className='filters-help-tooltip'>
                            <strong>Geen selectie:</strong> Toont alle gebruikers.
                            <br />
                            <strong>Reguliere gebruikers:</strong> Normale gebruikers (niet-admins).
                            <br />
                            <strong>Admins:</strong> Admin/support accounts.
                            <br />
                            <strong>Admin-impersonatie:</strong> Admin ingelogd als een andere gebruiker.
                        </div>
                    </div>
                    <Checkbox
                        checked={filters.includeRegularUsers}
                        onCheck={(checked: boolean) => onUpdateFilter('includeRegularUsers', checked)}
                        label='Reguliere gebruikers'
                    />
                    <Checkbox
                        checked={filters.includeAdmins}
                        onCheck={(checked: boolean) => onUpdateFilter('includeAdmins', checked)}
                        label='Admins'
                    />
                    <Checkbox
                        checked={filters.includeAdminAsUser}
                        onCheck={(checked: boolean) => onUpdateFilter('includeAdminAsUser', checked)}
                        label='Admin-impersonatie'
                    />
                </div>
                {filters.featureKey && (
                    <div className='filter-row drill-down-info'>
                        <span>
                            Drill-down: <strong>{filters.featureKey}</strong>
                        </span>
                        <BolmateButton
                            onClick={onClearDrillDown}
                            label='Wis filter'
                            secondary
                            ornaments={{element: <AwesomeIcon name='times' />, placement: ElementPosition.LEFT}}
                        />
                    </div>
                )}
            </div>
        );
    }

    // Global timeseries chart
    const globalChartData = useMemo(() => {
        if (!data?.timeseries_global?.length) return null;

        const categories = data.timeseries_global.map(p => p.period);
        const values = data.timeseries_global.map(p => p.value);
        const yAxisConfig = buildYAxisConfig(values);

        const options = buildBaseChartOptions(categories, yAxisConfig, 'Platform activiteit over tijd', 'line', {
            colors: ['#008FFB']
        });

        const series = [
            {
                name: filters.metric === 'unique_users' ? 'Unieke gebruikers' : 'Events',
                data: values
            }
        ];

        return {options, series};
    }, [data?.timeseries_global, filters.metric]);

    // Top categories line chart
    const topCategoriesChartData = useMemo(() => {
        if (!data?.timeseries_top?.length) return null;

        // Get all unique periods
        const allPeriods = new Set<string>();
        data.timeseries_top.forEach(series => {
            series.points.forEach(p => allPeriods.add(p.period));
        });
        const categories = Array.from(allPeriods).sort();

        // Build series data
        const series = data.timeseries_top.map(s => ({
            name: s.seriesKey,
            data: categories.map(period => {
                const point = s.points.find(p => p.period === period);
                return point?.value ?? 0;
            })
        }));

        const allValues = series.flatMap(s => s.data);
        const yAxisConfig = buildYAxisConfig(allValues);

        const title =
            filters.topN === 0
                ? `Alle ${filters.breakdown === 'feature_group' ? 'groepen' : 'features'}`
                : `Top ${filters.topN} ${filters.breakdown === 'feature_group' ? 'groepen' : 'features'}`;

        const options = buildBaseChartOptions(categories, yAxisConfig, title, 'line', {
            chart: {type: 'line', stacked: false, toolbar: {show: false}, zoom: {enabled: false}},
            markers: {size: 6, strokeWidth: 2, hover: {size: 8}},
            legend: {position: 'top', horizontalAlign: 'left'},
            tooltip: {shared: true, intersect: false}
        });

        return {options, series};
    }, [data?.timeseries_top, filters.topN, filters.breakdown]);

    // Drill-down chart
    const drilldownChartData = useMemo(() => {
        if (!data?.feature_drilldown) return null;
        const {page_views, actions} = data.feature_drilldown;

        if (!page_views?.length) return null;

        const categories = page_views.map(p => p.period);
        const pageViewValues = page_views.map(p => p.value);
        const actionValues = actions.map(p => p.value);

        const yAxisConfig = buildYAxisConfig([...pageViewValues, ...actionValues]);

        const options = buildBaseChartOptions(categories, yAxisConfig, `Detail: ${filters.featureKey}`, 'line', {
            yaxis: [
                {
                    ...yAxisConfig,
                    reversed: false,
                    title: {text: 'Events'},
                    labels: {formatter: (val: number) => Math.round(val).toString()}
                }
            ],
            legend: {position: 'top'},
            colors: ['#008FFB', '#00E396']
        });

        const series = [
            {name: 'Page views', data: pageViewValues},
            {name: 'Acties', data: actionValues}
        ];

        return {options, series};
    }, [data?.feature_drilldown, filters.featureKey]);

    // User growth chart (total registered users over time)
    const userGrowthChartData = useMemo(() => {
        if (!data?.timeseries_user_growth?.length) return null;

        const categories = data.timeseries_user_growth.map(p => p.period);
        const values = data.timeseries_user_growth.map(p => p.value);

        const yAxisConfig = buildYAxisConfig(values, {padding: 0.05, startFromZero: false});

        const options = buildBaseChartOptions(categories, yAxisConfig, 'Totaal gebruikers op platform', 'area', {
            fill: {
                type: 'gradient',
                gradient: {
                    shadeIntensity: 1,
                    opacityFrom: 0.4,
                    opacityTo: 0.1,
                    stops: [0, 100]
                }
            },
            colors: ['#00E396']
        });

        const series = [
            {
                name: 'Totaal gebruikers',
                data: values
            }
        ];

        return {options, series};
    }, [data?.timeseries_user_growth]);

    function renderTableHeaderHelp(tooltip: string) {
        return (
            <div className='filters-help-container'>
                <InfoIcon scale={0.6} />
                <div className='filters-help-tooltip'>{tooltip}</div>
            </div>
        );
    }

    function renderTopTable() {
        if (!data?.top_table?.length) return null;

        return (
            <div className='top-table-section'>
                <div className='top-table-header'>
                    <h3>
                        {filters.topN === 0 ? 'Alle' : `Top ${filters.topN}`}{' '}
                        {filters.breakdown === 'feature_group' ? 'groepen' : 'features'}
                    </h3>
                    {renderTableHeaderHelp(
                        'Deze tabel toont de meest gebruikte features of groepen op basis van de geselecteerde filters. ' +
                            'Klik op "Detail" om dieper in te zoomen op een specifieke feature. ' +
                            'Na het klikken op Detail kunt u nogmaals een subgroep selecteren om de weergave verder te verfijnen en een gedetailleerde grafiek te bekijken.'
                    )}
                </div>
                <table className='top-table'>
                    <thead>
                        <tr>
                            <th>
                                <span>Naam</span>
                                {renderTableHeaderHelp('De naam van de feature of groep.')}
                            </th>
                            <th>
                                <span>Groep</span>
                                {renderTableHeaderHelp(
                                    'De categorie waartoe deze feature behoort (bijv. Settings, Emails, Invoices).'
                                )}
                            </th>
                            <th>
                                <span>Views (totaal)</span>
                                {renderTableHeaderHelp(
                                    'Het totale aantal paginaweergaven voor deze feature in de geselecteerde periode.'
                                )}
                            </th>
                            <th>
                                <span>Views (uniek)</span>
                                {renderTableHeaderHelp('Het aantal unieke gebruikers dat deze pagina heeft bekeken.')}
                            </th>
                            <th>
                                <span>Acties (totaal)</span>
                                {renderTableHeaderHelp(
                                    'Het totale aantal uitgevoerde acties (bijv. opslaan, exporteren) voor deze feature.'
                                )}
                            </th>
                            <th>
                                <span>Acties (uniek)</span>
                                {renderTableHeaderHelp('Het aantal unieke gebruikers dat een actie heeft uitgevoerd.')}
                            </th>
                            <th>
                                <span>Conversie %</span>
                                {renderTableHeaderHelp(
                                    'Het percentage van paginaweergaven dat resulteerde in een actie. Formule: (Acties totaal / Views totaal) × 100.'
                                )}
                            </th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.top_table.map(item => (
                            <tr key={item.key}>
                                <td>{item.title}</td>
                                <td>{item.group}</td>
                                <td>{item.views_total}</td>
                                <td>{item.views_unique}</td>
                                <td>{item.actions_total}</td>
                                <td>{item.actions_unique}</td>
                                <td>{item.conversion_pct}%</td>
                                <td>
                                    <BolmateButton
                                        onClick={() => onDrillDown(item.key)}
                                        label='Detail'
                                        secondary
                                        ornaments={{
                                            element: <AwesomeIcon name='search' />,
                                            placement: ElementPosition.LEFT
                                        }}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    function formatTimeRemaining(seconds: number): string {
        if (seconds <= 0) return '0 min';
        const minutes = Math.ceil(seconds / 60);
        if (minutes < 60) return `${minutes} min`;
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        return remainingMinutes > 0 ? `${hours}u ${remainingMinutes}min` : `${hours}u`;
    }

    function formatCooldownTime(seconds: number): string {
        if (seconds <= 0) return '0s';
        if (seconds < 60) return `${seconds}s`;
        const minutes = Math.ceil(seconds / 60);
        return `${minutes} min`;
    }

    function renderAnalyticsSection() {
        const aggregationDelay = data?.aggregation_delay_minutes ?? 60;
        const cooldown = data?.aggregation_cooldown;
        // Use interactive countdown instead of server value
        const canRunAggregation = cooldownRemainingSeconds <= 0;
        const cooldownTotalMinutes = cooldown ? Math.ceil(cooldown.cooldown_total_seconds / 60) : 15;

        // Pending events info
        const readyEventsCount = cooldown?.ready_events_count ?? 0;
        const pendingEventsCount = cooldown?.pending_events_count ?? 0;
        const pendingReadyInSeconds = cooldown?.pending_ready_in_seconds ?? 0;

        return (
            <div className='analytics-section'>
                <div className='analytics-header'>
                    <div className='analytics-header-row'>
                        <h2>Usage Analytics</h2>
                        <div className='aggregation-button-container'>
                            <BolmateButton
                                onClick={onRunAggregation}
                                label={
                                    !canRunAggregation
                                        ? `Wacht ${formatCooldownTime(cooldownRemainingSeconds)}`
                                        : readyEventsCount > 0
                                          ? `Aggregeer ${readyEventsCount} events`
                                          : 'Aggregatie uitvoeren'
                                }
                                secondary
                                disabled={!canRunAggregation}
                                ornaments={{element: <AwesomeIcon name='cogs' />, placement: ElementPosition.LEFT}}
                            />
                            <div className='filters-help-container'>
                                <InfoIcon scale={0.6} />
                                <div className='filters-help-tooltip'>
                                    <strong>Handmatige aggregatie</strong>
                                    <br />
                                    <br />
                                    Deze knop voert direct de data-aggregatie uit, zonder te wachten op de automatische
                                    verwerking (elke {aggregationDelay} minuten).
                                    <br />
                                    <br />
                                    <strong>Vertraging:</strong> Events moeten minimaal {aggregationDelay} minuten oud
                                    zijn voordat ze kunnen worden geaggregeerd (om race conditions te voorkomen).
                                    <br />
                                    <br />
                                    <strong>Cooldown:</strong> Na het uitvoeren moet u {cooldownTotalMinutes} minuten
                                    wachten voordat u opnieuw kunt aggregeren. Dit geldt voor alle beheerders.
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='aggregation-info'>
                        <span className='info-section'>
                            Data wordt geaggregeerd met {aggregationDelay} minuten vertraging
                        </span>
                        {readyEventsCount > 0 && (
                            <span className='info-section'>
                                <strong>{readyEventsCount} events</strong> klaar voor aggregatie
                            </span>
                        )}
                        {pendingEventsCount > 0 && readyEventsCount === 0 && (
                            <span className='info-section'>
                                <strong>{pendingEventsCount} events</strong> wachtend (gereed over{' '}
                                {formatTimeRemaining(pendingReadyInSeconds)})
                            </span>
                        )}
                        {pendingEventsCount > 0 && readyEventsCount > 0 && (
                            <span className='info-section'>
                                <strong>{pendingEventsCount} events</strong> wachtend
                            </span>
                        )}
                    </div>
                </div>
                {renderFilters()}

                {!isDateRangeValid ? (
                    // Case 0: Invalid date range (fromDate > toDate)
                    <div className='no-data-message warning'>
                        <p>
                            <strong>Ongeldige datumbereik</strong>
                        </p>
                        <p>
                            De "Van" datum ({filters.fromDate}) is later dan de "Tot" datum ({filters.toDate}).
                        </p>
                        <p>Pas de datums aan zodat "Van" eerder is dan of gelijk aan "Tot".</p>
                        <BolmateButton
                            onClick={onResetFilters}
                            label='Filters resetten'
                            secondary
                            ornaments={{element: <AwesomeIcon name='undo' />, placement: ElementPosition.LEFT}}
                        />
                    </div>
                ) : !hasAnyData ? (
                    // Case 1: No data exists in the system at all
                    <div className='no-data-message'>
                        <p>Geen analytics data beschikbaar.</p>
                        <p>Analytics events worden automatisch verzameld zodra gebruikers de platform gebruiken.</p>
                        <p>Klik op de knop hieronder om verzamelde events te aggregeren.</p>
                        <BolmateButton
                            onClick={onRunAggregation}
                            label={
                                !canRunAggregation
                                    ? `Wacht ${formatCooldownTime(cooldownRemainingSeconds)}`
                                    : readyEventsCount > 0
                                      ? `Aggregeer ${readyEventsCount} events`
                                      : 'Aggregatie uitvoeren'
                            }
                            secondary
                            disabled={!canRunAggregation}
                            ornaments={{element: <AwesomeIcon name='play' />, placement: ElementPosition.LEFT}}
                        />
                    </div>
                ) : (
                    // Case 2 & 3: Data exists (may or may not have filtered results)
                    <>
                        {!hasFilteredResults ? (
                            // Case 2: Data exists but current filters return no results for Feature activiteit
                            <div className='no-data-message'>
                                <p>De huidige filterinstellingen geven geen resultaten voor Feature activiteit.</p>
                                <p>
                                    Mogelijk is de verzamelde data voor andere gebruikertypes dan geselecteerd in de
                                    sectie "Gebruikertype", of er zijn geen events in de geselecteerde periode.
                                </p>
                                <p>
                                    Probeer een andere periode, andere gebruikertypes te selecteren of reset de filters.
                                </p>
                                <BolmateButton
                                    onClick={onResetFilters}
                                    label='Filters resetten'
                                    secondary
                                    ornaments={{element: <AwesomeIcon name='undo' />, placement: ElementPosition.LEFT}}
                                />
                            </div>
                        ) : (
                            // Case 3: Filters return results - show Feature activiteit charts
                            <>
                                <div className='charts-section'>
                                    <div className='charts-section-header'>
                                        <h3>Feature activiteit</h3>
                                        <div className='filters-help-container'>
                                            <InfoIcon scale={0.6} />
                                            <div className='filters-help-tooltip'>
                                                Deze grafieken tonen het gebruik van features op basis van de
                                                geselecteerde filters.
                                                <br />
                                                <br />
                                                <strong>Platform activiteit:</strong> Toont{' '}
                                                {filters.metric === 'unique_users'
                                                    ? 'unieke gebruikers die features hebben gebruikt'
                                                    : 'totaal aantal events'}{' '}
                                                in de geselecteerde periode.
                                                <br />
                                                <br />
                                                <strong>Top groepen/features:</strong> Toont de meest populaire{' '}
                                                {filters.breakdown === 'feature_group' ? 'feature groepen' : 'features'}
                                                .
                                            </div>
                                        </div>
                                    </div>
                                    <div className='charts-grid'>
                                        <div className='chart-container'>
                                            {globalChartData ? (
                                                <Chart
                                                    options={globalChartData.options}
                                                    series={globalChartData.series}
                                                    type='line'
                                                    height={300}
                                                />
                                            ) : loading ? (
                                                <div className='chart-loading'>
                                                    <Loader active />
                                                </div>
                                            ) : (
                                                <div className='chart-no-data'>
                                                    <p>Geen data beschikbaar voor de geselecteerde periode.</p>
                                                </div>
                                            )}
                                        </div>

                                        <div className='chart-container'>
                                            {topCategoriesChartData ? (
                                                <Chart
                                                    options={topCategoriesChartData.options}
                                                    series={topCategoriesChartData.series}
                                                    type='line'
                                                    height={300}
                                                />
                                            ) : loading ? (
                                                <div className='chart-loading'>
                                                    <Loader active />
                                                </div>
                                            ) : (
                                                <div className='chart-no-data'>
                                                    <p>Geen data beschikbaar voor de geselecteerde periode.</p>
                                                </div>
                                            )}
                                        </div>

                                        {(drilldownChartData || (loading && filters.featureKey)) && (
                                            <div className='chart-container full-width'>
                                                {drilldownChartData ? (
                                                    <Chart
                                                        options={drilldownChartData.options}
                                                        series={drilldownChartData.series}
                                                        type='line'
                                                        height={300}
                                                    />
                                                ) : loading ? (
                                                    <div className='chart-loading'>
                                                        <Loader active />
                                                    </div>
                                                ) : (
                                                    <div className='chart-no-data'>
                                                        <p>Geen drill-down data beschikbaar.</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {renderTopTable()}
                            </>
                        )}

                        {/* Gebruikersgroei section - always visible when hasAnyData, independent of hasFilteredResults */}
                        <div className='charts-section user-growth-section'>
                            <div className='charts-section-header'>
                                <h3>Gebruikersgroei</h3>
                                <div className='filters-help-container'>
                                    <InfoIcon scale={0.6} />
                                    <div className='filters-help-tooltip'>
                                        Dit is het <strong>cumulatieve totaal</strong> van geregistreerde gebruikers op
                                        het platform.
                                        <br />
                                        <br />
                                        Deze grafiek toont het <strong>totale aantal accounts</strong>, niet hoeveel
                                        gebruikers een specifieke feature hebben gebruikt (zoals in "Feature activiteit"
                                        hierboven).
                                        <br />
                                        <br />
                                        Alleen de filters <strong>Van-Tot</strong> en <strong>Periode</strong> zijn van
                                        toepassing op deze grafiek.
                                    </div>
                                </div>
                            </div>
                            <div className='charts-grid'>
                                <div className='chart-container full-width'>
                                    {userGrowthChartData ? (
                                        <Chart
                                            options={userGrowthChartData.options}
                                            series={userGrowthChartData.series}
                                            type='area'
                                            height={300}
                                        />
                                    ) : loading ? (
                                        <div className='chart-loading'>
                                            <Loader active />
                                        </div>
                                    ) : (
                                        <div className='chart-no-data'>
                                            <p>Geen gebruikersgroei data beschikbaar voor de geselecteerde periode.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}
                <Loader active={loading} />
            </div>
        );
    }

    function renderAnalyticsSettings() {
        if (!analyticsSettings || !editableSettings) return null;

        const {data_counts, limits} = analyticsSettings;
        const totalRecords = data_counts.raw_events + data_counts.user_day_records + data_counts.daily_aggregates;
        const isDisabled = settingsLoading || settingsSaving;

        return (
            <div className='analytics-settings-section'>
                <div className='settings-header'>
                    <h3>Analytics Instellingen</h3>
                    <div className='filters-help-container'>
                        <InfoIcon scale={0.6} />
                        <div className='filters-help-tooltip'>
                            Beheer hier de analytics dataverzameling en -opslag.
                            <br />
                            <br />
                            <strong>Dataverzameling:</strong> Als uitgeschakeld, worden er geen nieuwe analytics events
                            opgeslagen.
                            <br />
                            <br />
                            <strong>Retentie:</strong> Na de opgegeven periode worden oude records automatisch
                            verwijderd.
                            <br />
                            <br />
                            <strong>Aggregation delay:</strong> Tijd voordat events worden geaggregeerd (voorkomt race
                            conditions).
                        </div>
                    </div>
                </div>
                <div className='settings-content'>
                    <div className='setting-row'>
                        <div className='setting-label'>
                            <span>Dataverzameling</span>
                            <span className='setting-description'>
                                {editableSettings.collection_enabled
                                    ? 'Analytics events worden opgeslagen'
                                    : 'Analytics events worden NIET opgeslagen'}
                            </span>
                        </div>
                        <Checkbox
                            checked={editableSettings.collection_enabled}
                            onCheck={(checked: boolean) => onUpdateEditableSetting('collection_enabled', checked)}
                            disabled={isDisabled}
                            label={editableSettings.collection_enabled ? 'Ingeschakeld' : 'Uitgeschakeld'}
                        />
                    </div>
                    <div className='retention-settings'>
                        <h4>Data Retentie & Aggregation</h4>
                        <div className='settings-grid'>
                            <div className='setting-input-row'>
                                <label>Ruwe events retentie (max {limits.max_raw_events_days} dagen)</label>
                                <div className='input-with-suffix'>
                                    <BolmateNumberInput
                                        value={editableSettings.raw_events_retention_days}
                                        onChange={(value: number) => {
                                            const v = Math.min(limits.max_raw_events_days, Math.max(1, value || 1));
                                            onUpdateEditableSetting('raw_events_retention_days', v);
                                        }}
                                        inputProps={{min: 1, max: limits.max_raw_events_days}}
                                        disabled={isDisabled}
                                        wholeNumbers
                                    />
                                    <span>dagen</span>
                                </div>
                                <span className='record-count'>{data_counts.raw_events.toLocaleString()} records</span>
                            </div>
                            <div className='setting-input-row'>
                                <label>Gebruiker-dag retentie (max {limits.max_user_day_days} dagen)</label>
                                <div className='input-with-suffix'>
                                    <BolmateNumberInput
                                        value={editableSettings.user_day_retention_days}
                                        onChange={(value: number) => {
                                            const v = Math.min(limits.max_user_day_days, Math.max(1, value || 1));
                                            onUpdateEditableSetting('user_day_retention_days', v);
                                        }}
                                        inputProps={{min: 1, max: limits.max_user_day_days}}
                                        disabled={isDisabled}
                                        wholeNumbers
                                    />
                                    <span>dagen</span>
                                </div>
                                <span className='record-count'>
                                    {data_counts.user_day_records.toLocaleString()} records
                                </span>
                            </div>
                            <div className='setting-input-row'>
                                <label>Dagelijkse aggregaten retentie (0 = onbeperkt)</label>
                                <div className='input-with-suffix'>
                                    <BolmateNumberInput
                                        value={editableSettings.daily_aggregates_retention_days}
                                        onChange={(value: number) => {
                                            const v = Math.max(0, value || 0);
                                            onUpdateEditableSetting('daily_aggregates_retention_days', v);
                                        }}
                                        inputProps={{min: 0}}
                                        disabled={isDisabled}
                                        wholeNumbers
                                    />
                                    <span>dagen</span>
                                </div>
                                <span className='record-count'>
                                    {data_counts.daily_aggregates.toLocaleString()} records
                                </span>
                            </div>
                            <div className='setting-input-row'>
                                <label>Aggregation delay (min 1 minuut)</label>
                                <div className='input-with-suffix'>
                                    <BolmateNumberInput
                                        value={editableSettings.aggregation_delay_minutes}
                                        onChange={(value: number) => {
                                            const v = Math.max(1, value || 1);
                                            onUpdateEditableSetting('aggregation_delay_minutes', v);
                                        }}
                                        inputProps={{min: 1}}
                                        disabled={isDisabled}
                                        wholeNumbers
                                    />
                                    <span>minuten</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='settings-actions'>
                        <BolmateButton
                            onClick={onSaveSettings}
                            label='Instellingen opslaan'
                            disabled={isDisabled || !settingsHasChanges}
                            ornaments={{element: <AwesomeIcon name='save' />, placement: ElementPosition.LEFT}}
                        />
                        <div className='spacer' />
                        {totalRecords === 0 && <span className='no-data-hint'>Er is geen data om te wissen</span>}
                        <BolmateButton
                            onClick={onOpenClearDataModal}
                            label='Alle data wissen'
                            secondary
                            color={'red'}
                            disabled={isDisabled || totalRecords === 0}
                            ornaments={{element: <AwesomeIcon name='trash' />, placement: ElementPosition.LEFT}}
                        />
                    </div>
                </div>
                <Loader active={settingsSaving} />
            </div>
        );
    }

    function renderClearDataModal() {
        if (!analyticsSettings) return null;

        const {data_counts} = analyticsSettings;
        const totalRecords = data_counts.raw_events + data_counts.user_day_records + data_counts.daily_aggregates;

        return (
            <WithBackground
                active={clearDataModalActive}
                onClose={onCloseClearDataModal}
                preventBackgroundClick={clearDataLoading}
                escapeDisabled={clearDataLoading}
            >
                <div className='default-popup clear-analytics-data'>
                    <div className='header-container'>
                        <label className='header-title'>Analytics data wissen</label>
                        <CloseButton onClick={onCloseClearDataModal} disabled={clearDataLoading} />
                    </div>
                    <div className='body-container'>
                        <Loader active={clearDataLoading} />
                        <p className='body-message'>
                            <strong>Let op:</strong> Deze actie verwijdert <b>alle ({totalRecords.toLocaleString()})</b>{' '}
                            analytics records permanent. Dit omvat:
                        </p>
                        <ul className='data-list'>
                            <li>{data_counts.raw_events.toLocaleString()} ruwe events</li>
                            <li>{data_counts.user_day_records.toLocaleString()} gebruiker-dag records</li>
                            <li>{data_counts.daily_aggregates.toLocaleString()} dagelijkse aggregaten</li>
                        </ul>
                        <p className='body-message warning'>
                            <AwesomeIcon name='exclamation-triangle' /> Deze actie kan <b>niet</b> ongedaan worden
                            gemaakt. Alle historische analytics data gaat verloren.
                        </p>
                        <Checkbox
                            checked={clearDataConfirmed}
                            onCheck={(checked: boolean) => onSetClearDataConfirmed(checked)}
                            label='Ik bevestig dat alle analytics data permanent verwijderd moet worden en begrijp dat dit niet teruggedraaid kan worden'
                        />
                    </div>
                    <div className='footer-container btn-2'>
                        <BolmateButton onClick={onCloseClearDataModal} label='Annuleren' disabled={clearDataLoading} />
                        <BolmateButton
                            onClick={onClearAllData}
                            label='Data wissen'
                            disabled={clearDataLoading || !clearDataConfirmed}
                            secondary
                        />
                    </div>
                </div>
            </WithBackground>
        );
    }

    return (
        <div className='platform-usage'>
            <div className='header'>
                <BolmateButton
                    onClick={onClickBack}
                    label='Terug'
                    disabled={loading}
                    secondary
                    ornaments={{element: <AwesomeIcon name='arrow-left' />, placement: ElementPosition.LEFT}}
                />
                <h2>Platformgebruik</h2>
                <BolmateButton
                    onClick={onRefresh}
                    label='Vernieuwen'
                    disabled={loading}
                    secondary
                    ornaments={{element: <AwesomeIcon name='sync' />, placement: ElementPosition.LEFT}}
                />
            </div>
            <div className='content'>
                {loading && !data && <Loader active />}
                {data && (
                    <>
                        <div className='snapshot-stats'>
                            {renderEmailStats()}
                            {renderNotificationStats()}
                            {renderAdditionalUsersStats()}
                        </div>
                        {renderAnalyticsSection()}
                        {renderAnalyticsSettings()}
                    </>
                )}
            </div>
            {renderClearDataModal()}
        </div>
    );
}
